import type { Directive, DirectiveBinding } from 'vue';

export interface LongPressElement extends HTMLElement {
  _longPressTimer?: ReturnType<typeof setTimeout>;
  _longPressFired?: boolean;
  _longPressStartHandler?: (e: MouseEvent | TouchEvent) => void;
  _longPressCancelHandler?: () => void;
  _longPressClickHandler?: (e: MouseEvent) => void;
}

// The function to be passed as the directive's value
type LongPressHandler = (e: MouseEvent | TouchEvent) => void;

export const vLongPress: Directive<LongPressElement, LongPressHandler> = {
  mounted(el: LongPressElement, binding: DirectiveBinding<LongPressHandler>) {
    // Get the duration from the directive argument (e.g., v-long-press:1000) or use a default
    const duration = binding.arg ? parseInt(binding.arg, 10) : 500;
    const handler = binding.value;

    if (typeof handler !== 'function') {
      return;
    }

    const start = (e: MouseEvent | TouchEvent) => {
      // Reset the flag at the beginning of every press
      el._longPressFired = false;

      if (e.type === 'mousedown' && (e as MouseEvent).button !== 0) {
        return;
      }

      el._longPressTimer = setTimeout(() => {
        handler(e);
        // Set the flag to true once the long press action is fired
        el._longPressFired = true;
      }, duration);
    };

    const cancel = () => {
      if (el._longPressTimer) {
        clearTimeout(el._longPressTimer);
      }
    };

    // Handler to intercept the click event
    const clickCaptureHandler = (e: MouseEvent) => {
      if (el._longPressFired) {
        // If a long press just happened, stop the click event
        e.stopPropagation();
        e.preventDefault();
      }
    };

    // Store handlers on the element for cleanup
    el._longPressStartHandler = start;
    el._longPressCancelHandler = cancel;
    el._longPressClickHandler = clickCaptureHandler;

    // Attach pointer event listeners
    el.addEventListener('mousedown', start);
    el.addEventListener('touchstart', start);
    el.addEventListener('mouseup', cancel);
    el.addEventListener('mouseleave', cancel);
    el.addEventListener('touchend', cancel);

    // Attach the click listener in the CAPTURE phase
    el.addEventListener('click', clickCaptureHandler, true);
  },

  unmounted(el: LongPressElement) {
    // Cleanup: remove all event listeners
    if (el._longPressStartHandler) {
      el.removeEventListener('mousedown', el._longPressStartHandler);
      el.removeEventListener('touchstart', el._longPressStartHandler);
    }
    if (el._longPressCancelHandler) {
      el.removeEventListener('mouseup', el._longPressCancelHandler);
      el.removeEventListener('mouseleave', el._longPressCancelHandler);
      el.removeEventListener('touchend', el._longPressCancelHandler);
    }
    // Cleanup the click listener
    if (el._longPressClickHandler) {
      el.removeEventListener('click', el._longPressClickHandler, true);
    }
  },
};
